import React, { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary, ResponseErrorPanel } from '@backstage/core-components';
import { Grid } from '@mui/material';
import { useAzureQueryResources } from '../../hooks/useQueryAzure';
import { KeyValueFilterComponent } from '../KeyValueFilter';
import {
  ComponentVersion,
  ComponentVersionTag,
  LatestTag,
} from '@internal/backstage-plugin-bidone-backend';
import { VersionTableComponent } from './VersionTable';
import { useBitbucketTags } from '../../hooks/useBitbucket';
import { findEntityRefByRepo, useFetchEntities } from '../../hooks/useCatalog';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  customComponentMap, //
  AZURE_TAG_GIT_TAG_OR_BRANCH,
  PERMA_TAG_ENVIRONMENT,
  PERMA_TAG_PORTFOLIO,
  PERMA_TAG_DEPLOYMENTGROUP,
  PERMA_TAG_REGION,
  PERMA_TAG_REPO,
  DEPLOYMENT_GROUPS,
} from './types';
import {
  useConfiguredEnvironments,
  useConfiguredRegions,
} from '../../hooks/useCommon';
import { useStaticContentLatestVersion } from '../../hooks/useStaticContent';

const checkTagMatching = (
  bitbucketTag: LatestTag,
  deployedTag: string,
  env: string,
): boolean => {
  // UAT could have hotfixes deployed as well, which are prod tags
  if (env === 'uat') {
    return (
      bitbucketTag['uat'] === deployedTag ||
      bitbucketTag['prod'] === deployedTag
    );
  }
  return bitbucketTag[env] === deployedTag;
};

const VersionFetchDataComponent = () => {
  // Fetch available envs and regions
  const { data: envs, error: fetchEnvsError } = useConfiguredEnvironments();
  const { data: regions, error: fetchRegionsError } = useConfiguredRegions();

  // Default filters
  const [tagFilters, setTagFilters] = useState<{ [key: string]: string }>({
    [PERMA_TAG_PORTFOLIO]: 'bdirect',
    [PERMA_TAG_DEPLOYMENTGROUP]: 'shop-apis',
    [PERMA_TAG_ENVIRONMENT]: 'prod',
  });

  // Set filters to use a set of values (dropdown) instead of free text
  const [filterValueSets, setFilterValueSets] = useState<
    Record<string, string[]>
  >({
    [PERMA_TAG_ENVIRONMENT]: [],
    [PERMA_TAG_DEPLOYMENTGROUP]: [...DEPLOYMENT_GROUPS],
    [PERMA_TAG_REGION]: [],
  });

  // Watch and update the filter value sets for envs and regions
  useEffect(() => {
    if (envs) {
      setFilterValueSets(prev => ({
        ...prev,
        [PERMA_TAG_ENVIRONMENT]: envs.map(x => x.toLowerCase()),
      }));
    }

    if (regions) {
      setFilterValueSets(prev => ({
        ...prev,
        [PERMA_TAG_REGION]: regions.map(x => x.toLowerCase()),
      }));
    }
  }, [regions, envs]);

  // Fetch onboarded components
  const catalogApi = useApi(catalogApiRef); // Rule of hooks, we need to pass the catalogApi from the top level
  const { data: components, error: fetchEntitiesError } = useFetchEntities(
    'component',
    catalogApi,
  );

  // Fetch Azure resources by tags
  const {
    data: azureResources,
    isFetching: isAzureFetching,
    error: fetchAzureError,
  } = useAzureQueryResources(tagFilters);

  const repos = useMemo(() => {
    if (!azureResources) {
      return [];
    }
    const result = azureResources
      .map(x => x.tags[PERMA_TAG_REPO] || x.tags['GitRepo'])
      .filter(x => x !== undefined);

    // Extra custom mappings for components those not deployed to Azure
    Object.keys(customComponentMap).forEach(deploymentGroup => {
      if (
        tagFilters[PERMA_TAG_DEPLOYMENTGROUP] === deploymentGroup &&
        customComponentMap[deploymentGroup]
      ) {
        Object.values(customComponentMap[deploymentGroup]).forEach(repo => {
          if (result.includes(repo)) {
            return; // Already exists
          }
          result.push(repo);
        });
      }
    });

    return result;
  }, [azureResources]);

  // Fetch Bitbucket tags
  const {
    data: bitbucketTags,
    isFetching: isBitbucketFetching,
    error: fetchBitbucketError,
  } = useBitbucketTags(repos);

  // Fetch MFE versions
  const {
    data: mfeVersion,
    isFetching: isMfeVersionFetching,
    error: fetchMfeVersionError,
  } = useStaticContentLatestVersion(
    'bdirect-mfe',
    'js',
    tagFilters[PERMA_TAG_ENVIRONMENT],
    tagFilters[PERMA_TAG_DEPLOYMENTGROUP],
  );

  // Main model to pass to the table
  // This needs to map the result from multiple sources
  const versions = useMemo<Record<string, ComponentVersion>>(
    () => {
      if (!azureResources) {
        return {};
      }

      const result: Record<string, ComponentVersion> = {};
      // Preload the result with the available regions
      // This is to keep the view consistent with all regions lined up
      const azureTags: Record<string, ComponentVersionTag> = {};
      regions?.forEach(region => {
        envs?.forEach(env => {
          // If env filters applied, skip the rest
          if (
            tagFilters[PERMA_TAG_ENVIRONMENT] &&
            tagFilters[PERMA_TAG_ENVIRONMENT] !== env
          ) {
            return;
          }
          // If region filters applied, skip the rest
          if (
            tagFilters[PERMA_TAG_REGION] &&
            tagFilters[PERMA_TAG_REGION] !== region
          ) {
            return;
          }
          azureTags[`${region}-${env}`] = {
            azureTag: '-',
            azureLink: '',
          };
        });
      });

      azureResources.forEach(x => {
        const { name: nameWithConvention, type, id, tags } = x;
        const region = `${tags[PERMA_TAG_REGION]?.toLowerCase() ?? ''}`;
        const env = `${tags[PERMA_TAG_ENVIRONMENT]?.toLowerCase() ?? ''}`;

        // Trim the region and env from the resource name
        const name = nameWithConvention
          .toLowerCase()
          .replace(`${region}-`, '')
          .replace(`-${env}`, '');

        // Initial value
        if (!result[name]) {
          result[name] = {
            name,
            type,
            azureTags,
          };
        }

        // Do not process tags not in the defined envs or regions
        if (!envs?.includes(env) || !regions?.includes(region)) {
          return;
        }

        const regionEnv = `${region}-${env}`;
        const bitbucketTag =
          (bitbucketTags &&
            bitbucketTags[tags[PERMA_TAG_REPO] || tags['GitRepo']]) ||
          {};

        const azureLink =
          'https://portal.azure.com/#@bidone.co.nz/resource' + id;
        const azureTag =
          tags.gitTagOrBranch /* backward compatible */ ||
          tags[AZURE_TAG_GIT_TAG_OR_BRANCH] ||
          '-';

        // Merge values
        result[name] = {
          ...result[name],
          bitbucketTag,
          entityRef: findEntityRefByRepo(
            components,
            tags[PERMA_TAG_REPO]?.toLowerCase() ||
              tags['GitRepo']?.toLowerCase(),
          ),
          azureTags: {
            ...result[name].azureTags,
            [regionEnv]: {
              azureTag,
              azureLink,
              isDeployed: !!azureTag,
              tagMatched: checkTagMatching(bitbucketTag, azureTag, env),
            },
          },
        };
      });

      // Static content components
      const allStaticVersions = {
        ...mfeVersion,
      };

      Object.keys(customComponentMap).forEach(deploymentGroup => {
        if (
          tagFilters[PERMA_TAG_DEPLOYMENTGROUP] !== deploymentGroup ||
          !customComponentMap[deploymentGroup]
        ) {
          return; // Skip if not in the current deployment group filter
        }

        // Each deployment group
        Object.keys(customComponentMap[deploymentGroup]).forEach(component => {
          if (result[component]) {
            return; // Already exists
          }

          if (!allStaticVersions[component]) {
            return; // No static version available
          }

          const componentRepo = customComponentMap[deploymentGroup][component];

          const azureTags: Record<string, ComponentVersionTag> = {};
          const bitbucketTag = bitbucketTags?.[componentRepo] || {};

          // Each custom component in group
          Object.keys(allStaticVersions[component]).forEach(regionEnv => {
            const componentDeployment = allStaticVersions[component][regionEnv];
            const azureTag = componentDeployment?.tag ?? '-';
            let buildId = componentDeployment?.buildNumber;
            let azureLink = '';

            // Try to extract buildId from the buildNumber
            if (buildId) {
              buildId = buildId.split('_').pop()?.split('.')[0] || '';
            }
            // Then create a link to DeOps Azure DevOps build results
            if (buildId) {
              azureLink = `https://dev.azure.com/bidone/BidvestDirect/_build/results?buildId=${buildId}&view=results`;
            }

            azureTags[regionEnv] = {
              azureTag,
              azureLink,
              isDeployed: !!componentDeployment?.tag,
              tagMatched: checkTagMatching(
                bitbucketTag,
                azureTag,
                regionEnv.split('-')[1], // Extract env from regionEnv
              ),
            };
          });

          result[component] = {
            name: component,
            type: 'static-content',
            azureTags,
            bitbucketTag,
            entityRef: findEntityRefByRepo(components, componentRepo),
          } as ComponentVersion;
        });
      });

      return result;
    },
    // Dependencies
    [
      azureResources, //
      bitbucketTags,
      components,
      regions,
      envs,
      tagFilters,
      mfeVersion,
    ],
  );

  return (
    <Grid container>
      <Grid item md={3} lg={2}>
        <KeyValueFilterComponent
          keyLabel="Tags"
          keyOptions={[
            PERMA_TAG_PORTFOLIO,
            PERMA_TAG_DEPLOYMENTGROUP,
            PERMA_TAG_ENVIRONMENT,
            PERMA_TAG_REGION,
            PERMA_TAG_REPO,
            AZURE_TAG_GIT_TAG_OR_BRANCH,
            'GitCommitHash',
            'BuildId',
            'BuildReason',
            'BuildQueuedBy',
            'DeployDateUtc',
          ]}
          defaultFilters={tagFilters}
          filterValueSets={filterValueSets}
          onFilterChange={setTagFilters}
        />
      </Grid>
      <Grid item md={9} lg={10}>
        {fetchAzureError && (
          <ResponseErrorPanel
            defaultExpanded
            error={fetchAzureError}
            title="Fetching component versions"
          />
        )}
        {fetchBitbucketError && (
          <ResponseErrorPanel
            defaultExpanded
            error={fetchBitbucketError}
            title="Fetching bitbucket tags"
          />
        )}
        {fetchEntitiesError && (
          <ResponseErrorPanel
            defaultExpanded
            error={fetchEntitiesError}
            title="Fetching components"
          />
        )}
        {fetchRegionsError && (
          <ResponseErrorPanel
            defaultExpanded
            error={fetchRegionsError}
            title="Fetching regions list"
          />
        )}
        {fetchEnvsError && (
          <ResponseErrorPanel
            defaultExpanded
            error={fetchEnvsError}
            title="Fetching environments list"
          />
        )}
        {fetchMfeVersionError && (
          <ResponseErrorPanel
            error={fetchMfeVersionError}
            title="Fetching latest MFE version"
          />
        )}
        {!fetchAzureError && (
          <VersionTableComponent
            env={tagFilters[PERMA_TAG_ENVIRONMENT]}
            versions={versions}
            loading={isAzureFetching}
            tagLoading={isBitbucketFetching}
            allLoading={
              isAzureFetching || isBitbucketFetching || isMfeVersionFetching
            }
          />
        )}
      </Grid>
    </Grid>
  );
};

/** @public */
export const ComponentVersionDashboard = () => {
  return (
    <ErrorBoundary>
      <VersionFetchDataComponent />
    </ErrorBoundary>
  );
};
